/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUserSessionIssuerCimdClientsRequest,
  ListUserSessionIssuerCimdClientsSecurity,
} from "../models/operations/listusersessionissuercimdclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionIssuerCimdClientsInfiniteQuery,
  buildUserSessionIssuerCimdClientsQuery,
  prefetchUserSessionIssuerCimdClients,
  prefetchUserSessionIssuerCimdClientsInfinite,
  queryKeyUserSessionIssuerCimdClients,
  queryKeyUserSessionIssuerCimdClientsInfinite,
  UserSessionIssuerCimdClientsInfiniteQueryData,
  UserSessionIssuerCimdClientsPageParams,
  UserSessionIssuerCimdClientsQueryData,
} from "./userSessionIssuerCimdClients.core.js";
export {
  buildUserSessionIssuerCimdClientsInfiniteQuery,
  buildUserSessionIssuerCimdClientsQuery,
  prefetchUserSessionIssuerCimdClients,
  prefetchUserSessionIssuerCimdClientsInfinite,
  queryKeyUserSessionIssuerCimdClients,
  queryKeyUserSessionIssuerCimdClientsInfinite,
  type UserSessionIssuerCimdClientsInfiniteQueryData,
  type UserSessionIssuerCimdClientsPageParams,
  type UserSessionIssuerCimdClientsQueryData,
};

export type UserSessionIssuerCimdClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUserSessionIssuerCimdClients userSessionIssuersCimdClients
 *
 * @remarks
 * List the custom CIMD document URLs configured on a user_session_issuer. Does not include the preset catalog — call listPresets for that.
 */
export function useUserSessionIssuerCimdClients(
  request: ListUserSessionIssuerCimdClientsRequest,
  security?: ListUserSessionIssuerCimdClientsSecurity | undefined,
  options?: QueryHookOptions<
    UserSessionIssuerCimdClientsQueryData,
    UserSessionIssuerCimdClientsQueryError
  >,
): UseQueryResult<
  UserSessionIssuerCimdClientsQueryData,
  UserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionIssuerCimdClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionIssuerCimdClients userSessionIssuersCimdClients
 *
 * @remarks
 * List the custom CIMD document URLs configured on a user_session_issuer. Does not include the preset catalog — call listPresets for that.
 */
export function useUserSessionIssuerCimdClientsSuspense(
  request: ListUserSessionIssuerCimdClientsRequest,
  security?: ListUserSessionIssuerCimdClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionIssuerCimdClientsQueryData,
    UserSessionIssuerCimdClientsQueryError
  >,
): UseSuspenseQueryResult<
  UserSessionIssuerCimdClientsQueryData,
  UserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionIssuerCimdClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionIssuerCimdClients userSessionIssuersCimdClients
 *
 * @remarks
 * List the custom CIMD document URLs configured on a user_session_issuer. Does not include the preset catalog — call listPresets for that.
 */
export function useUserSessionIssuerCimdClientsInfinite(
  request: ListUserSessionIssuerCimdClientsRequest,
  security?: ListUserSessionIssuerCimdClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsPageParams
  >,
  UserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsQueryError,
    InfiniteData<
      UserSessionIssuerCimdClientsInfiniteQueryData,
      UserSessionIssuerCimdClientsPageParams
    >,
    QueryKey,
    UserSessionIssuerCimdClientsPageParams
  >({
    ...buildUserSessionIssuerCimdClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listUserSessionIssuerCimdClients userSessionIssuersCimdClients
 *
 * @remarks
 * List the custom CIMD document URLs configured on a user_session_issuer. Does not include the preset catalog — call listPresets for that.
 */
export function useUserSessionIssuerCimdClientsInfiniteSuspense(
  request: ListUserSessionIssuerCimdClientsRequest,
  security?: ListUserSessionIssuerCimdClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsPageParams
  >,
  UserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    UserSessionIssuerCimdClientsInfiniteQueryData,
    UserSessionIssuerCimdClientsQueryError,
    InfiniteData<
      UserSessionIssuerCimdClientsInfiniteQueryData,
      UserSessionIssuerCimdClientsPageParams
    >,
    QueryKey,
    UserSessionIssuerCimdClientsPageParams
  >({
    ...buildUserSessionIssuerCimdClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setUserSessionIssuerCimdClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionIssuerCimdClientsQueryData,
): UserSessionIssuerCimdClientsQueryData | undefined {
  const key = queryKeyUserSessionIssuerCimdClients(...queryKeyBase);

  return client.setQueryData<UserSessionIssuerCimdClientsQueryData>(key, data);
}

export function invalidateUserSessionIssuerCimdClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "userSessionIssuersCimdClients",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllUserSessionIssuerCimdClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionIssuersCimdClients", "list"],
  });
}
